import type { FunctionComponent, ReactNode } from 'react';

import type {
  BlocklistConfidence,
  BlocklistMatch,
  BlocklistMatchType,
} from '@ballerine/business-analysis-schemas';
import { isValidBlocklistUrlValue } from '@ballerine/business-analysis-schemas';

import { ctw } from '@/common';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/molecules/Accordion';

const MATCH_TYPE_LABELS: Record<BlocklistMatchType, string> = {
  url: 'URL',
  company_name: 'Company Name',
  registration_number: 'Reg. Number',
  address: 'Address',
};

// Exact Figma pill tones (node 14221:6225/6383) — the shared severity map reads too washed-out here.
const CONFIDENCE_PILL_CLASSES: Record<BlocklistConfidence, string> = {
  high: 'bg-[#df2222]/[0.12] text-[#df2222]',
  medium: 'bg-[#ffb35a]/10 text-[#e7922e]',
};

// Only values that pass the shared PSL host validation AND clamp to http(s) become clickable —
// anything else (javascript:, data:, //evil.com, legacy garbage like "foobar") renders as plain
// text. Defense-in-depth on the last hop before an <a href> in the analyst's browser.
const toExternalHref = (value: string): string | null => {
  if (!isValidBlocklistUrlValue(value)) return null;

  const candidate =
    value.startsWith('http://') || value.startsWith('https://') ? value : `https://${value}`;

  try {
    const url = new URL(candidate);

    return url.protocol === 'http:' || url.protocol === 'https:' ? candidate : null;
  } catch {
    return null;
  }
};

const MatchValue: FunctionComponent<{ match: BlocklistMatch; value: string }> = ({
  match,
  value,
}) => {
  const href = match.matchType === 'url' ? toExternalHref(value) : null;

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="break-words text-sm font-medium tracking-[-0.15px] text-[#007aff] hover:underline"
      >
        {value}
      </a>
    );
  }

  return (
    <span className="break-words text-sm font-medium tracking-[-0.15px] text-[#14203d]">
      {value}
    </span>
  );
};

const ColumnHeader: FunctionComponent<{ children: ReactNode; className?: string }> = ({
  children,
  className,
}) => <p className={ctw('text-xs font-bold uppercase text-[#97a3b6]', className)}>{children}</p>;

type BlocklistMatchListProps = {
  matches: BlocklistMatch[];
};

export const BlocklistMatchList: FunctionComponent<BlocklistMatchListProps> = ({ matches }) => {
  return (
    <div className="space-y-3">
      {matches.map((match, index) => {
        const accordionValue = `match-${index}`;
        const fieldLabel = MATCH_TYPE_LABELS[match.matchType] ?? match.matchType;

        return (
          <div
            key={accordionValue}
            data-testid="blocklist-match"
            className="rounded-lg border border-slate-200 bg-white shadow-sm"
          >
            <Accordion type="single" collapsible className="w-full" defaultValue={accordionValue}>
              <AccordionItem value={accordionValue} className="border-none">
                <AccordionTrigger className="rounded-lg px-4 py-3 hover:bg-slate-50 hover:no-underline data-[state=open]:rounded-b-none">
                  <div className="flex w-full items-center gap-4 pr-2 text-left">
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#f4f6fd] text-xs font-semibold text-[#97a3b6]">
                      {index + 1}
                    </span>
                    <span className="shrink-0 text-xs font-bold uppercase text-[#97a3b6]">
                      Blocking Reason
                    </span>
                    <p className="min-w-0 flex-1 break-words text-sm font-medium tracking-[-0.15px] text-[#14203d]">
                      {match.blockingReason ?? match.matchedValue}
                    </p>
                    <span
                      className={ctw(
                        'rounded px-2 py-0.5 text-xs font-medium uppercase',
                        CONFIDENCE_PILL_CLASSES[match.confidence],
                      )}
                    >
                      {match.confidence}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="border-t border-slate-100 px-4 pb-4 pt-4">
                  {/* One field pair per match today; Phase 2 multi-property records (BAL-7983) add more rows to this grid. */}
                  <div className="grid grid-cols-[minmax(90px,auto),1fr,minmax(90px,auto),1fr,auto] items-center gap-x-4 gap-y-3 text-sm">
                    <ColumnHeader className="col-span-2">Blocked Entity</ColumnHeader>
                    <ColumnHeader className="col-span-2">Matched On</ColumnHeader>
                    <ColumnHeader>Source</ColumnHeader>

                    <span className="text-xs text-[#97a3b6]">{fieldLabel}</span>
                    {/* Matches persisted before BAL-8116 carry no blockedValue — left empty until
                        backfilled. Deliberately truthy: an empty-string blockedValue renders the
                        same empty cell as absent (writers only emit non-empty entity values). */}
                    {match.blockedValue ? (
                      <MatchValue match={match} value={match.blockedValue} />
                    ) : (
                      <span />
                    )}
                    <span className="text-xs text-[#97a3b6]">{fieldLabel}</span>
                    <MatchValue match={match} value={match.matchedValue} />
                    <span className="justify-self-start rounded-[3px] bg-[#f4f6fd] px-1.5 py-0.5 text-xs font-medium text-[#97a3b6]">
                      Merchant Entity
                    </span>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        );
      })}
    </div>
  );
};
